import { Hexagon, ClipboardList, Handshake, Key, ArrowRight } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef, MouseEvent } from "react";

const steps = [
  {
    icon: ClipboardList,
    title: "List",
    body: "Free home valuation and a 1-on-1 intake with your Listing Concierge.",
  },
  {
    icon: Handshake,
    title: "Show & Negotiate",
    body: "Verified buyers, guided offers, and clear next steps at every turn.",
  },
  {
    icon: Key,
    title: "Close",
    body: "Paperwork handled, milestones tracked, and just 0.50% at closing.",
  },
];

const StepCard = ({
  step,
  index,
}: {
  step: (typeof steps)[number];
  index: number;
}) => {
  const Icon = step.icon;
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-50, 50], [8, -8]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(x, [-50, 50], [-8, 8]), { stiffness: 200, damping: 20 });

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      style={{ rotateX, rotateY, transformPerspective: 1000 }}
      whileHover={{ y: -6 }}
      className="group relative bg-card border border-border rounded-2xl p-8 shadow-sm hover:shadow-xl hover:border-primary/50 transition-[box-shadow,border-color] duration-300 text-center cursor-default overflow-hidden"
    >
      {/* Animated gradient sheen */}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />

      {/* Floating step number badge */}
      <motion.div
        initial={{ scale: 0, rotate: -90 }}
        whileInView={{ scale: 1, rotate: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.15 + 0.3, type: "spring", stiffness: 200 }}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center shadow-md"
      >
        {index + 1}
      </motion.div>

      <div className="relative inline-flex items-center justify-center mb-5">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute"
        >
          <Hexagon className="h-20 w-20 text-primary/15" strokeWidth={1.25} />
        </motion.div>
        <motion.div
          whileHover={{ scale: 1.15 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="relative"
        >
          <Icon className="h-8 w-8 text-primary group-hover:text-accent transition-colors duration-300" strokeWidth={1.5} />
        </motion.div>
        {/* Pulse ring on hover */}
        <span className="absolute inset-0 m-auto h-20 w-20 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping bg-primary/10" />
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">
        Step {index + 1}
      </div>
      <h3 className="text-xl font-bold text-foreground mb-3">{step.title}</h3>
      <p className="text-muted-foreground leading-relaxed">{step.body}</p>

      {/* Reveal arrow on hover */}
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        whileHover={{ opacity: 1, x: 0 }}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <span>Learn more</span>
        <ArrowRight className="h-4 w-4" />
      </motion.div>
    </motion.div>
  );
};

const ThreeStepFlow = () => {
  return (
    <section className="bg-brand-warm-50 py-10 md:py-14 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 space-y-3"
        >
          <span className="text-sm font-medium uppercase tracking-wider text-primary">How it works</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground">
            Three steps.{" "}
            <motion.span
              initial={{ backgroundSize: "0% 100%" }}
              whileInView={{ backgroundSize: "100% 100%" }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              style={{
                backgroundImage: "linear-gradient(transparent 65%, hsl(var(--primary) / 0.2) 65%)",
                backgroundRepeat: "no-repeat",
              }}
              className="text-primary inline-block"
            >
              That's it.
            </motion.span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We promise it: every key action on REALV3 is three steps or three clicks. Selling your home is no exception.
          </p>
        </motion.div>

        <div className="relative grid md:grid-cols-3 gap-6 md:gap-8">
          {/* Animated connector line on desktop */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
            style={{ transformOrigin: "left" }}
            className="hidden md:block absolute top-1/2 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent -z-0"
          />
          {steps.map((s, i) => (
            <div key={s.title} className="relative z-10">
              <StepCard step={s} index={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ThreeStepFlow;
